import React, { Component } from "react"
import PropTypes from "prop-types"
import Radium from "higher-order-components/configured-radium/"
import { childParser } from "containers/utilities"
import { HeadingBlock, BackgroundImage, LayeredBackgroundImageAutoplay } from "containers"
import { WysiwygContent, VideoBackground } from "components"

/** Render a fifty fifty block */
class FiftyFiftyBlock extends Component {
  /**
   * @param {String} [preHeading] - Pre-Heading text.
   * @param {String} [heading] - Heading text.
   * @param {String} [blurb] - blurb text as xhtml string.
   * @param {'left'|'right'|'center'} [textAlign=left] - Text alignment.
   * @param {'small'|'medium'|'large'} [size] - Size of component.
   * @param {'top'|'bottom'|'center'} [textVerticalAlign=top] - Vertical text alignment.
   * @param {String} imageUrl - Image url.
   * @param {String} [backgroundColor='#f5f5f5'] - Background color as HTML hex code.
   * @param {'left'|'right'} [imagePosition=left] - Image position.
   * @param {'true'|'false'} [imagePositionMobile=false] - true means image should be on top always
   * @param {Array|Button} [components] - Array of nested components.
   * @returns {ReactElement} React Component
   */
  render() {
    const {
      imagePosition,
      imagePositionMobile,
      size,
      ratio,
      backgroundColor,
      heading,
      preHeading,
      textAlign,
      headingUppercase,
      videoUrl,
      imageUrl,
      backgroundImageArray,
      interval,
      loop,
      overlay = false,
      imageAlign,
      backgroundSize,
      blurb,
      textVerticalAlign
    } = this.props
    const leftRatio = imagePosition === "right" ? 100 - ratio : ratio
    const rightRatio = imagePosition !== "right" ? 100 - ratio : ratio
  let minHeight = "40vw"
  switch (size) {
  case "small":
    minHeight = "20vw"
    break
  case "medium":
    minHeight = "30vw"
    break
  case "disable":
    minHeight = "initial"
    break
  }
    const Tag = "div"

    return (
      <section style={[styles.base, { backgroundColor }]} data-test="fifty-fifty-block">
        <div style={[styles.columns, { minHeight }]}>
          <div
            style={[
              styles.row(`${leftRatio}%`, !imagePosition === "right", !imagePositionMobile),
              { flexShrink: 0 }
            ]}>
            <div style={styles.fitHeight}>
              {videoUrl ? (
                <VideoBackground src={videoUrl} poster={imageUrl} />
              ) : (
                backgroundImageArray && backgroundImageArray.length > 1 ?
                  <LayeredBackgroundImageAutoplay
                    interval={interval}
                    loop={loop}
                    imageSrcArray={backgroundImageArray}
                    backgroundSize="cover" /> :
                  <BackgroundImage
                    fill
                    src={imageUrl}
                    backgroundPosition={imageAlign}
                    backgroundSize={backgroundSize}>
                    <img src={imageUrl} alt={heading} style={[styles.image, { minHeight }]} />
                  </BackgroundImage>
              )}
            </div>
          </div>
          <div
            style={[
              styles.row(`${rightRatio}%`, imagePosition === "right", imagePositionMobile),
              styles.textAlign(textAlign)
            ]}>
            <div style={styles.verticalAlign(textVerticalAlign)}>
              <Tag>
                {heading && (
                  <HeadingBlock
                    tag="h3"
                    uppercase={headingUppercase}
                    value={heading}
                    preHeading={preHeading} />
                )}
                {blurb && <WysiwygContent xmlString={blurb} textAlign={textAlign} />}
                {childParser(this.props)}
              </Tag>
            </div>
          </div>
        </div>
      </section>
    )
  }
}

FiftyFiftyBlock.propTypes = {
  preHeading: PropTypes.string,
  heading: PropTypes.string,
  blurb: PropTypes.string,
  textAlign: PropTypes.oneOf(["left", "right", "center"]),
  textVerticalAlign: PropTypes.oneOf(["top", "bottom", "center"]),
  imageUrl: PropTypes.string.isRequired,
  videoUrl: PropTypes.string,
  imagePosition: PropTypes.oneOf(["left", "right"]),
  imagePositionMobile: PropTypes.bool,
  imageAlign: PropTypes.string,
  headingUppercase: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large", "disable"]),
  ratio: PropTypes.number,
  backgroundSize: PropTypes.oneOf(["cover", "contain"]),
  backgroundColor: PropTypes.string
}

FiftyFiftyBlock.defaultProps = {
  backgroundColor: "#f5f5f5",
  ratio: 50
}

export default Radium(FiftyFiftyBlock)

const styles = {
  base: {
    display: "block",
    width: "100%",
    flex: "1 1 auto",
    color: "#101010",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    "@media (min-width: 820px)": {
      display: "flex"
    }
  },
  columns: {
    flex: "1 1 auto",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    "@media (min-width: 820px)": {
      flexDirection: "row",
      alignItems: "stretch"
    }
  },
  row: (width, displayOnLeft, imagePositionMobile) => ({
    order: imagePositionMobile ? 2 : 1,
    display: "flex",
    width: "100%",
    "@media (min-width: 820px)": {
      width: width,
      position: "relative",
      order: displayOnLeft ? 1 : 2
    }
  }),
  // Fix for older Safari / Chrome where internal elements did not fill height of parent
  // elements that stretched to flex container
  fitHeight: {
    width: "100%",
    zIndex: 0,
    "@media (min-width: 820px)": {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0
    }
  },
  textAlign: alignment => ({
    "@media (min-width: 820px)": {
      textAlign: alignment
    },
    textAlign: "center"
  }),
  verticalAlign: alignment => ({
    padding: "2em 1em 1em",
    display: "flex",
    justifyContent: alignment === "bottom" ? "flex-end" : alignment === "top" ? "top" : "center",
    flexDirection: "column",
    flex: "1 1 100%",
    boxSizing: "border-box"
  }),
  image: {
    width: "100%",
    visibility: "hidden",
    "@media (min-width: 820px)": {
      height: "100%"
    }
  }
}



// WEBPACK FOOTER //
// ./src/containers/fifty-fifty-block/index.jsx